import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MailCheck } from "lucide-react";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      setMessage(data.message || "If an account exists for that email, we've sent a password reset link.");
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't send the reset email right now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <AuthShell>
        <div className="text-center" data-testid="forgot-sent">
          <MailCheck className="mx-auto h-12 w-12 text-emerald-500" />
          <h2 className="mt-6 font-display text-2xl font-bold tracking-tight">Check your inbox</h2>
          <p className="mt-2 text-sm text-muted-foreground">{message}</p>
          <p className="mt-1 text-xs text-muted-foreground">The link expires in 1 hour. Don't forget to check spam.</p>
          <Button asChild className="mt-6" data-testid="forgot-back-login">
            <Link to="/login">Back to login</Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div data-testid="forgot-password-page">
        <p className="gx-label">Forgot password</p>
        <h2 className="mt-2 font-display text-3xl font-bold tracking-tight lg:text-4xl">
          Reset via email
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Enter the work email on your account. We'll send a one-hour reset link — the same flow as Pravadh IQ and PM.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="forgot-email">Email</Label>
            <Input
              id="forgot-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="h-11"
              autoFocus
              data-testid="forgot-email-input"
            />
          </div>

          {error && (
            <p
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              data-testid="forgot-error"
            >
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={submitting || !email.trim()}
            className="h-11 w-full text-sm font-semibold"
            data-testid="forgot-submit-button"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
          </Button>
        </form>

        <p className="mt-6 text-sm text-muted-foreground">
          Remembered it?{" "}
          <Link
            to="/login"
            className="font-semibold text-primary underline-offset-4 hover:underline"
            data-testid="forgot-to-login"
          >
            Back to login
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}
